import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faShieldHeart,
  faFileMedical,
  faPills,
  faTriangleExclamation,
  faLightbulb,
  faClockRotateLeft,
  faChartLine,
  faStar,
  faRotate,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faFileLines,
  faDumbbell,
  faStethoscope,
  faAppleWhole,
  faPersonRunning,
  faBed,
  faBinoculars,
  faSpinner,
  faCalendarDays,
  faArrowUp,
  faArrowDown,
  faMinus,
  faScaleBalanced,
  faDatabase,
  faSyringe,
  faHospital,
  faClipboardList,
  faShieldHalved,
  faUserDoctor,
  faBell,
  faCheck,
  faTimes,
  faClock,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import healthSummaryService, {
  HealthSummaryData,
  HealthSummaryListItem,
  RiskFlag,
  Recommendation,
  VitalOverview,
} from '../../services/healthSummaryService';
import paymentService, {
  InsightSchedule,
} from '../../services/paymentService';
import './HealthSummary.css';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton/BackButton';
import { useServiceWallet } from '../../components/ServiceWallet/WalletContext';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';

const RISK_COLORS: Record<string, string> = { low: '#10b981', moderate: '#f59e0b', high: '#f97316', critical: '#ef4444' };
const DISEASE_LABELS: Record<string, string> = {
  diabetes: 'Diabetes', cardiovascular: 'Cardiovascular', hypertension: 'Hypertension',
  obesity: 'Obesity', respiratory: 'Respiratory', mental_health: 'Mental Health',
  liver: 'Liver', kidney: 'Kidney', thyroid: 'Thyroid', metabolic_syndrome: 'Metabolic Syndrome',
};

const WEEKLY_DAY_MIN = 1;
const WEEKLY_DAY_MAX = 7;
const MONTH_DAY_MIN = 1;
const MONTH_DAY_MAX = 28;

const MONTH_DAYS = Array.from(
  { length: MONTH_DAY_MAX - MONTH_DAY_MIN + 1 },
  (_, i) => i + MONTH_DAY_MIN,
);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const nth = (day: number) => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatLocalDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const HealthSummary: React.FC = () => {
  const { t } = useTranslation(['health', 'common']);
  const navigate = useNavigate();
  const { activeProfile } = useProfile();
const { canAccess } = useServiceAccess();
  const [creditNotice, setCreditNotice] = useState<{ required: number; current: number } | null>(null);
  const [creditCost, setCreditCost] = useState(1);
  const [diseaseRisk, setDiseaseRisk] = useState<any>(null);
  const [summary, setSummary] = useState<HealthSummaryData | null>(null);
  const [history, setHistory] = useState<HealthSummaryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'vitals' | 'comparison' | 'rawdata' | 'history'>('overview');

  // Date range state
  const [dateRangeType, setDateRangeType] = useState<'preset' | 'custom'>('preset');
  const [presetDays, setPresetDays] = useState(90);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Comparison state
  const [previousSummary, setPreviousSummary] = useState<HealthSummaryData | null>(null);

  // Credits state (balance lives in the shared header wallet)
  const {
    balance: creditsBalance,
    refresh: refreshCredits,
    openCredits,
  } = useServiceWallet();

  useEffect(() => {
    let cancelled = false;
    paymentService
      .getCreditCosts()
      .then((costs) => {
        if (!cancelled && costs.health_insights) setCreditCost(costs.health_insights);
      })
      .catch(() => {
        // Keep the documented default when the cost endpoint is unavailable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Schedule state
  const [schedule, setSchedule] = useState<InsightSchedule | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState<'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [weeklyDay, setWeeklyDay] = useState(WEEKLY_DAY_MIN);
  const [monthDay, setMonthDay] = useState(MONTH_DAY_MIN);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const applyScheduleToForm = useCallback((data: InsightSchedule | null) => {
    setScheduleFrequency(data?.frequency ?? 'monthly');
    setScheduleEnabled(data?.is_enabled ?? false);
    setWeeklyDay(clamp(data?.preferred_day ?? WEEKLY_DAY_MIN, WEEKLY_DAY_MIN, WEEKLY_DAY_MAX));
    setMonthDay(clamp(data?.preferred_day ?? MONTH_DAY_MIN, MONTH_DAY_MIN, MONTH_DAY_MAX));
  }, []);

  const activeScheduleDay = scheduleFrequency === 'weekly' ? weeklyDay : monthDay;

  const scheduleIsDirty = (() => {
    if (!schedule) return scheduleFrequency !== 'monthly' || scheduleEnabled || monthDay !== MONTH_DAY_MIN;
    if (scheduleFrequency !== schedule.frequency) return true;
    if (scheduleEnabled !== schedule.is_enabled) return true;
    const savedDay =
      schedule.frequency === 'weekly'
        ? clamp(schedule.preferred_day, WEEKLY_DAY_MIN, WEEKLY_DAY_MAX)
        : clamp(schedule.preferred_day, MONTH_DAY_MIN, MONTH_DAY_MAX);
    return activeScheduleDay !== savedDay;
  })();

  const fetchSchedule = useCallback(async () => {
    try {
      const data = await paymentService.getSchedule();
      if (data) {
        setSchedule(data);
        applyScheduleToForm(data);
      }
    } catch {
      // No schedule
    }
  }, [applyScheduleToForm]);

  const describeScheduleError = (err: any): string => {
    const data = err?.response?.data;

    if (err?.response?.status === 403) {
      return data?.error || 'Credits are required to configure insight schedules.';
    }
    if (typeof data === 'string' && data) return data;
    if (data && typeof data === 'object') {
      const messages = Object.values(data as Record<string, unknown>)
        .flat()
        .filter((value): value is string => typeof value === 'string');
      if (messages.length) return messages.join(' ');
    }
    return 'Could not save your schedule. Please try again.';
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    setScheduleError(null);
    try {
      const data = await paymentService.updateSchedule({
        frequency: scheduleFrequency,
        is_enabled: scheduleEnabled,
        preferred_day: activeScheduleDay,
      });
      setSchedule(data);
      applyScheduleToForm(data);
      setShowScheduleModal(false);
    } catch (err: any) {
      setScheduleError(describeScheduleError(err));
    } finally {
      setScheduleSaving(false);
    }
  };

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      const data = await healthSummaryService.getLatestSummary();
      setSummary(data);

      // If still processing, poll for updates
      if (data.generation_status === 'pending' || data.generation_status === 'processing') {
        setGenerating(true);
        pollForCompletion(data.id);
      } else {
        setGenerating(false);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError('Failed to load health insights');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const pollForCompletion = (id: number) => {
    const interval = setInterval(async () => {
      try {
        const data = await healthSummaryService.getSummaryDetail(id);
        if (data.generation_status === 'completed' || data.generation_status === 'failed') {
          clearInterval(interval);
          setSummary(data);
          setGenerating(false);
          fetchHistory();
        }
      } catch {
        clearInterval(interval);
        setGenerating(false);
      }
    }, 3000);

    setTimeout(() => {
      clearInterval(interval);
      setGenerating(false);
    }, 120000);
  };

  const fetchHistory = async () => {
    try {
      const data = await healthSummaryService.getSummaryHistory();
      setHistory(data);
    } catch {
      // Silently fail
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setCreditNotice(null);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (dateRangeType === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else if (dateRangeType === 'preset') {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - presetDays);
        startDate = start.toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
      }

      const data = await healthSummaryService.generateSummary(startDate, endDate);
      setSummary(data);
      refreshCredits();
      if (data.generation_status === 'pending' || data.generation_status === 'processing') {
        pollForCompletion(data.id);
      } else {
        setGenerating(false);
        fetchHistory();
      }
    } catch (err: any) {
      const payload = err.response?.data;
      if (payload?.code === 'insufficient_credits') {
        setCreditNotice({
          required: typeof payload.required === 'number' ? payload.required : creditCost,
          current:
            typeof payload.current_balance === 'number'
              ? payload.current_balance
              : creditsBalance ?? 0,
        });
        refreshCredits();
      } else {
        setError(payload?.error || 'Failed to generate insights');
      }
      setGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (creditsBalance === null || creditsBalance < creditCost) {
      openCredits();
      return;
    }
    void handleGenerate();
  };

  const loadHistoryItem = async (id: number) => {
    try {
      setLoading(true);
      const data = await healthSummaryService.getSummaryDetail(id);
      setSummary(data);
      setActiveTab('overview');
    } catch {
      setError('Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  // Fetch comparison data when switching to comparison tab
  const fetchComparison = useCallback(async () => {
    if (!summary || history.length < 2) {
      setPreviousSummary(null);
      return;
    }
    // Find the previous completed summary (not the current one)
    const prevItem = history.find(h => h.id !== summary.id && h.generation_status === 'completed');
    if (prevItem) {
      try {
        const data = await healthSummaryService.getSummaryDetail(prevItem.id);
        setPreviousSummary(data);
      } catch {
        setPreviousSummary(null);
      }
    }
  }, [summary, history]);

  useEffect(() => {
    fetchLatest();
    fetchHistory();
    fetchSchedule();
  }, [fetchLatest, fetchSchedule, activeProfile]);

  useEffect(() => {
    if (activeTab === 'comparison') {
      fetchComparison();
    }
  }, [activeTab, fetchComparison, activeProfile]);

  // Fetch disease risk prediction
  useEffect(() => {
    const fetchRisk = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.latest);
        if (res.data.has_prediction) setDiseaseRisk(res.data);
      } catch { /* non-critical */ }
    };
    fetchRisk();
  }, [activeProfile]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#06b6d4';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreGradient = (score: number) => {
    if (score >= 80) return 'linear-gradient(135deg, #10b981, #059669)';
    if (score >= 60) return 'linear-gradient(135deg, #06b6d4, #0891b2)';
    if (score >= 40) return 'linear-gradient(135deg, #f59e0b, #d97706)';
    return 'linear-gradient(135deg, #ef4444, #dc2626)';
  };

  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'high': return faCircleExclamation;
      case 'medium': return faTriangleExclamation;
      default: return faCircleInfo;
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const getVitalStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return '#10b981';
      case 'elevated': return '#f59e0b';
      case 'low': return '#3b82f6';
      case 'concerning': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getRecIcon = (category: string) => {
    switch (category) {
      case 'diet': return faAppleWhole;
      case 'exercise': return faPersonRunning;
      case 'lifestyle': return faBed;
      case 'medical': return faStethoscope;
      case 'monitoring': return faBinoculars;
      default: return faLightbulb;
    }
  };

  // Helpers for comparison
  const getChangeIndicator = (current: number, previous: number) => {
    const diff = current - previous;
    const pct = previous !== 0 ? Math.round((diff / previous) * 100) : 0;
    if (diff > 0) return { icon: faArrowUp, color: '#10b981', label: `+${pct}%`, diff: `+${diff}` };
    if (diff < 0) return { icon: faArrowDown, color: '#ef4444', label: `${pct}%`, diff: `${diff}` };
    return { icon: faMinus, color: '#94a3b8', label: 'No change', diff: '0' };
  };

  const getTrendLabel = (trend: string) => {
    switch (trend) {
      case 'increasing': return { icon: faArrowUp, color: '#f59e0b', label: 'Increasing' };
      case 'decreasing': return { icon: faArrowDown, color: '#3b82f6', label: 'Decreasing' };
      case 'stable': return { icon: faMinus, color: '#10b981', label: 'Stable' };
      default: return { icon: faMinus, color: '#94a3b8', label: 'N/A' };
    }
  };

  // Format vital name for display
  const formatVitalName = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const hasCredits = creditsBalance !== null && creditsBalance >= creditCost;

  if (loading && !summary) {
    return (
      <div className="hs-page">
        <div className="hs-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading health insights...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <section className="hs-back-banner">
      <div className="hs-back-banner-inner">
        <BackButton />
        {/* Header */}
        <div className="hs-header">
          <div className="hs-header-left">
            <FontAwesomeIcon icon={faHeartPulse} className="hs-header-icon" />
            <div>
              <h1>Health Insights</h1>
              <p>AI-powered analysis of your complete health profile</p>
            </div>
          </div>
          <div className="hs-header-actions">
            <button
              className="hs-schedule-btn"
              onClick={() => {
                applyScheduleToForm(schedule);
                setScheduleError(null);
                setShowScheduleModal(true);
              }}
            >
              <FontAwesomeIcon icon={faBell} /> Schedule
            </button>
            <button
              className="hs-generate-btn"
              onClick={handleGenerateClick}
              disabled={generating}
              title={hasCredits ? undefined : t('services:wallet.addCreditsToGenerate')}
            >
              <FontAwesomeIcon icon={generating ? faSpinner : faRotate} spin={generating} />
              {generating
                ? 'Generating...'
                : !hasCredits
                ? t('services:wallet.addCreditsToGenerate')
                : summary
                ? t('services:wallet.regenerateInsights', { cost: creditCost })
                : t('services:wallet.generateInsights', { cost: creditCost })}
            </button>
          </div>
        </div>
      </div>
    </section>
    <div className="hs-page">

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="hs-modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="hs-modal hs-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="hs-modal-header">
              <h2>Schedule Insights</h2>
              <button className="hs-modal-close" onClick={() => setShowScheduleModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p className="hs-modal-subtitle">Set up automatic health insight generation</p>

            <div className="hs-schedule-form">
              <div className="hs-schedule-toggle-row">
                <span>Enable automatic generation</span>
                <label className="hs-toggle">
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={e => setScheduleEnabled(e.target.checked)}
                  />
                  <span className="hs-toggle-slider" />
                </label>
              </div>

              <div className="hs-schedule-field">
                <label>Frequency</label>
                <div className="hs-schedule-freq-btns">
                  {(['weekly', 'monthly', 'quarterly'] as const).map(freq => (
                    <button
                      key={freq}
                      className={`hs-freq-btn ${scheduleFrequency === freq ? 'active' : ''}`}
                      onClick={() => setScheduleFrequency(freq)}
                    >
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hs-schedule-field">
                <label>
                  {scheduleFrequency === 'weekly' ? 'Day of Week' : 'Day of Month'}
                </label>
                {scheduleFrequency === 'weekly' ? (
                  <div className="hs-schedule-day-btns">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`hs-day-btn ${weeklyDay === i + 1 ? 'active' : ''}`}
                        onClick={() => setWeeklyDay(i + 1)}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="hs-schedule-month-grid">
                    {MONTH_DAYS.map(d => (
                      <button
                        key={d}
                        type="button"
                        className={`hs-day-btn hs-day-btn-month ${monthDay === d ? 'active' : ''}`}
                        onClick={() => setMonthDay(d)}
                        aria-pressed={monthDay === d}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                )}
                {scheduleFrequency !== 'weekly' && (
                  <p className="hs-schedule-hint">
                    {scheduleFrequency === 'quarterly'
                      ? `Runs on the ${monthDay}${nth(monthDay)} in January, April, July and October`
                      : `Runs on the ${monthDay}${nth(monthDay)} of every month`}
                  </p>
                )}
              </div>

              {scheduleError && (
                <div className="hs-error hs-schedule-error">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  <span>{scheduleError}</span>
                </div>
              )}

              {scheduleEnabled &&
                (scheduleIsDirty ? (
                  <div className="hs-schedule-next">
                    <FontAwesomeIcon icon={faClock} />
                    Save to confirm your next generation date
                  </div>
                ) : (
                  schedule?.next_run_date && (
                    <div className="hs-schedule-next">
                      <FontAwesomeIcon icon={faClock} />
                      Next generation: {formatLocalDate(schedule.next_run_date)}
                    </div>
                  )
                ))}

              <button
                className="hs-schedule-save-btn"
                onClick={handleSaveSchedule}
                disabled={scheduleSaving}
              >
                {scheduleSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                {scheduleSaving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="hs-date-range-section">
        <div className="hs-date-range-header">
          <FontAwesomeIcon icon={faCalendarDays} />
          <span>Data Range for Analysis</span>
        </div>
        <div className="hs-date-range-controls">
          <div className="hs-date-range-toggle">
            <button
              className={`hs-range-toggle-btn ${dateRangeType === 'preset' ? 'active' : ''}`}
              onClick={() => setDateRangeType('preset')}
            >
              Preset
            </button>
            <button
              className={`hs-range-toggle-btn ${dateRangeType === 'custom' ? 'active' : ''}`}
              onClick={() => setDateRangeType('custom')}
            >
              Custom
            </button>
          </div>

          {dateRangeType === 'preset' ? (
            <div className="hs-preset-buttons">
              {[
                { label: '30 Days', value: 30 },
                { label: '60 Days', value: 60 },
                { label: '90 Days', value: 90 },
                { label: '6 Months', value: 180 },
                { label: '1 Year', value: 365 },
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`hs-preset-btn ${presetDays === opt.value ? 'active' : ''}`}
                  onClick={() => setPresetDays(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="hs-custom-date-inputs">
              <div className="hs-date-input-group">
                <label>From</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={e => setCustomStartDate(e.target.value)}
                  max={customEndDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="hs-date-input-group">
                <label>To</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={e => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {creditNotice && (
        <div className="hs-error hs-credit-notice">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>
            {t('services:wallet.insufficientNotice', {
              required: creditNotice.required,
              current: creditNotice.current,
            })}
          </span>
          <button type="button" className="hs-credit-notice-btn" onClick={openCredits}>
            <FontAwesomeIcon icon={faPlus} />
            {t('services:wallet.addCredits')}
          </button>
        </div>
      )}

      {error && (
        <div className="hs-error">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{error}</span>
        </div>
      )}

      {generating && (
        <div className="hs-processing-banner">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Analyzing your health data, running document OCR, and generating AI summary. This may take a moment...</span>
        </div>
      )}

      {!summary && !generating && (
        <div className="hs-empty">
          <FontAwesomeIcon icon={faFileMedical} size="3x" />
          <h2>No Health Insights Yet</h2>
          <p>Generate your first AI-powered health summary to get insights about your overall health, risk factors, and personalized recommendations.</p>
          <button className="hs-generate-btn primary" onClick={handleGenerateClick}>
            <FontAwesomeIcon icon={faHeartPulse} />
            Generate My Health Insights
          </button>
          <p className="hs-credit-hint">
            {t('services:wallet.costHint', { cost: creditCost })}
          </p>
        </div>
      )}

      {summary && summary.generation_status === 'completed' && (
        <>
          {/* Tabs */}
          <div className="hs-tabs">
            <button
              className={`hs-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FontAwesomeIcon icon={faChartLine} />
              Overview
            </button>
            <button
              className={`hs-tab ${activeTab === 'vitals' ? 'active' : ''}`}
              onClick={() => setActiveTab('vitals')}
            >
              <FontAwesomeIcon icon={faHeartPulse} />
              Vitals
            </button>
            <button
              className={`hs-tab ${activeTab === 'comparison' ? 'active' : ''}`}
              onClick={() => setActiveTab('comparison')}
            >
              <FontAwesomeIcon icon={faScaleBalanced} />
              Compare
            </button>
            <button
              className={`hs-tab ${activeTab === 'rawdata' ? 'active' : ''}`}
              onClick={() => setActiveTab('rawdata')}
            >
              <FontAwesomeIcon icon={faDatabase} />
              Raw Data
            </button>
            <button
              className={`hs-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              History
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="hs-content">
              {/* Health Score Card */}
              <div className="hs-score-card">
                <div
                  className="hs-score-circle"
                  style={{ background: getScoreGradient(summary.health_score) }}
                >
                  <span className="hs-score-number">{summary.health_score}</span>
                  <span className="hs-score-max">/100</span>
                </div>
                <div className="hs-score-info">
                  <h2 style={{ color: getScoreColor(summary.health_score) }}>
                    {summary.health_score_label}
                  </h2>
                  <p className="hs-score-date">
                    Generated on {new Date(summary.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'long', year: 'numeric',
                    })}
                    {summary.date_range_start && summary.date_range_end && (
                      <span className="hs-date-range-badge">
                        {' '}| Data from {new Date(summary.date_range_start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' '} to {new Date(summary.date_range_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </p>
                  <div className="hs-data-sources">
                    {summary.data_sources.map((src) => (
                      <span key={src} className="hs-source-tag">{src.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* AI Summary */}
              {summary.ai_summary && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faFileMedical} />
                    <h3>Health Overview</h3>
                  </div>
                  <div className="hs-ai-summary">
                    {summary.ai_summary.split('\n\n').map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Flags */}
              {summary.risk_flags && summary.risk_flags.length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faShieldHeart} />
                    <h3>Risk Flags</h3>
                  </div>
                  <div className="hs-risk-list">
                    {summary.risk_flags.map((flag: RiskFlag, i: number) => (
                      <div key={i} className={`hs-risk-item risk-${flag.level}`}>
                        <FontAwesomeIcon
                          icon={getRiskIcon(flag.level)}
                          style={{ color: getRiskColor(flag.level) }}
                        />
                        <div>
                          <strong>{flag.flag}</strong>
                          <p>{flag.recommendation}</p>
                        </div>
                        <span className={`hs-risk-badge ${flag.level}`}>{flag.level}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Disease Risk Prediction */}
              {canAccess('disease-prediction') && diseaseRisk && diseaseRisk.disease_details && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <h3>Disease Risk Prediction</h3>
                    <span
                      style={{
                        marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600,
                        padding: '3px 10px', borderRadius: 12,
                        background: `${RISK_COLORS[diseaseRisk.overall_risk_category] || '#94a3b8'}18`,
                        color: RISK_COLORS[diseaseRisk.overall_risk_category] || '#94a3b8',
                        textTransform: 'capitalize',
                      }}
                    >
                      Overall: {diseaseRisk.overall_risk_score}/100 — {diseaseRisk.overall_risk_category}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginTop: 8 }}>
                    {Object.entries(diseaseRisk.disease_details as Record<string, any>)
                      .sort(([, a]: any, [, b]: any) => (b.score || 0) - (a.score || 0))
                      .map(([disease, detail]: [string, any]) => {
                        const color = RISK_COLORS[detail.category] || '#94a3b8';
                        return (
                          <div
                            key={disease}
                            style={{
                              background: '#fff', borderRadius: 10, padding: '12px 14px',
                              border: `1px solid ${color}25`, cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onClick={() => navigate('/disease-prediction')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                                {DISEASE_LABELS[disease] || disease}
                              </span>
                              <span style={{
                                fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: 8, background: `${color}15`, color,
                                textTransform: 'capitalize',
                              }}>
                                {detail.category}
                              </span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', borderRadius: 3, background: color,
                                width: `${Math.min(detail.score || 0, 100)}%`,
                                transition: 'width 0.8s ease',
                              }} />
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
                              {detail.score}/100
                              {detail.contributing_factors?.length > 0 && ` · ${detail.contributing_factors.slice(0, 2).join(', ')}`}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <div style={{ textAlign: 'right', marginTop: 10 }}>
                    <button
                      onClick={() => navigate('/disease-prediction')}
                      style={{
                        background: 'none', border: 'none', color: '#0d9488',
                        fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                      }}
                    >
                      View Full Disease Risk Report →
                    </button>
                  </div>
                </div>
              )}

              {/* Positive Notes */}
              {summary.activity_summary?.positive_notes &&
                summary.activity_summary.positive_notes.length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faStar} />
                    <h3>What You're Doing Well</h3>
                  </div>
                  <div className="hs-positive-list">
                    {summary.activity_summary.positive_notes.map((note: string, i: number) => (
                      <div key={i} className="hs-positive-item">
                        <FontAwesomeIcon icon={faCircleCheck} />
                        <span>{note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {summary.ai_recommendations && summary.ai_recommendations.length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faLightbulb} />
                    <h3>Recommendations</h3>
                  </div>
                  <div className="hs-rec-grid">
                    {summary.ai_recommendations.map((rec: Recommendation, i: number) => (
                      <div key={i} className={`hs-rec-card priority-${rec.priority}`}>
                        <div className="hs-rec-icon">
                          <FontAwesomeIcon icon={getRecIcon(rec.category)} />
                        </div>
                        <div className="hs-rec-body">
                          <span className="hs-rec-category">{rec.category}</span>
                          <p>{rec.recommendation}</p>
                        </div>
                        <span className={`hs-priority-dot ${rec.priority}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Document Insights */}
              {summary.document_insights && summary.document_insights.length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faFileLines} />
                    <h3>Document Insights (OCR)</h3>
                  </div>
                  <div className="hs-doc-insights">
                    {summary.document_insights.map((doc, i) => (
                      <div key={i} className="hs-doc-item">
                        <strong>{doc.source}</strong>
                        <p>{doc.insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Vitals Tab */}
          {activeTab === 'vitals' && (
            <div className="hs-content">
              {/* Vitals Overview */}
              {summary.vitals_summary?.overview && summary.vitals_summary.overview.length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faHeartPulse} />
                    <h3>Vitals Status</h3>
                  </div>
                  <div className="hs-vitals-grid">
                    {summary.vitals_summary.overview.map((v: VitalOverview, i: number) => (
                      <div key={i} className="hs-vital-card">
                        <div
                          className="hs-vital-status-dot"
                          style={{ background: getVitalStatusColor(v.status) }}
                        />
                        <h4>{v.vital}</h4>
                        <p>{v.detail}</p>
                        <span
                          className="hs-vital-badge"
                          style={{
                            color: getVitalStatusColor(v.status),
                            background: `${getVitalStatusColor(v.status)}15`,
                          }}
                        >
                          {v.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medications */}
              {(summary.medications_summary?.active?.length || summary.medications_summary?.recent?.length) ? (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faPills} />
                    <h3>Medications</h3>
                  </div>
                  {summary.medications_summary.active && summary.medications_summary.active.length > 0 && (
                    <>
                      <h4 className="hs-subsection-title">Active Medications</h4>
                      <div className="hs-med-list">
                        {summary.medications_summary.active.map((med, i) => (
                          <div key={i} className="hs-med-item active">
                            <FontAwesomeIcon icon={faPills} />
                            <div>
                              <strong>{med.name}</strong>
                              <span>{med.type} {med.dosage && `- ${med.dosage}`}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {summary.medications_summary.recent && summary.medications_summary.recent.length > 0 && (
                    <>
                      <h4 className="hs-subsection-title">Recent Medications</h4>
                      <div className="hs-med-list">
                        {summary.medications_summary.recent.map((med, i) => (
                          <div key={i} className="hs-med-item">
                            <FontAwesomeIcon icon={faPills} />
                            <div>
                              <strong>{med.name}</strong>
                              <span>{med.type} {med.dosage && `- ${med.dosage}`}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              {/* Medical History */}
              {summary.medical_history_summary && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faStethoscope} />
                    <h3>Medical History</h3>
                  </div>

                  {summary.medical_history_summary.conditions &&
                    summary.medical_history_summary.conditions.length > 0 && (
                    <div className="hs-history-group">
                      <h4 className="hs-subsection-title">Medical Conditions</h4>
                      {summary.medical_history_summary.conditions.map((c, i) => (
                        <div key={i} className="hs-history-item">
                          <span className={`hs-condition-status ${c.status}`}>{c.status}</span>
                          <span>{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {summary.medical_history_summary.allergies &&
                    summary.medical_history_summary.allergies.length > 0 && (
                    <div className="hs-history-group">
                      <h4 className="hs-subsection-title">Allergies</h4>
                      {summary.medical_history_summary.allergies.map((a, i) => (
                        <div key={i} className="hs-history-item">
                          <span className={`hs-allergy-severity ${a.severity}`}>{a.severity}</span>
                          <span>{a.name} ({a.type})</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {summary.medical_history_summary.surgeries &&
                    summary.medical_history_summary.surgeries.length > 0 && (
                    <div className="hs-history-group">
                      <h4 className="hs-subsection-title">Past Surgeries</h4>
                      {summary.medical_history_summary.surgeries.map((s, i) => (
                        <div key={i} className="hs-history-item">
                          <span className="hs-surgery-date">{s.date || 'N/A'}</span>
                          <span>{s.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Wearable Activity */}
              {summary.activity_summary?.wearable?.has_wearable && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faDumbbell} />
                    <h3>Wearable Activity</h3>
                  </div>
                  <div className="hs-wearable-grid">
                    {Object.entries(summary.activity_summary.wearable)
                      .filter(([key]) => key !== 'has_wearable')
                      .map(([key, data]: [string, any]) => (
                        <div key={key} className="hs-wearable-card">
                          <h4>{key.replace(/_/g, ' ')}</h4>
                          {data.latest && (
                            <div className="hs-wearable-value">
                              {data.latest.avg || data.latest.total || '-'}
                              <span className="hs-wearable-unit">{data.latest.unit}</span>
                            </div>
                          )}
                          <p>{data.days_of_data} days of data</p>
                          {data.average && <p className="hs-wearable-avg">Avg: {data.average}</p>}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== Comparison Tab ===== */}
          {activeTab === 'comparison' && (
            <div className="hs-content">
              {!previousSummary ? (
                <div className="hs-section">
                  <div className="hs-empty-text">
                    <FontAwesomeIcon icon={faScaleBalanced} style={{ fontSize: '2rem', color: '#cbd5e1', marginBottom: 12, display: 'block' }} />
                    <p>Need at least 2 completed reports to show comparison. Generate another report to unlock this feature.</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Score Comparison */}
                  <div className="hs-section">
                    <div className="hs-section-header">
                      <FontAwesomeIcon icon={faChartLine} />
                      <h3>Health Score Comparison</h3>
                    </div>
                    <div className="hs-compare-scores">
                      <div className="hs-compare-score-card">
                        <span className="hs-compare-label">Previous</span>
                        <div className="hs-compare-score-circle" style={{ background: getScoreGradient(previousSummary.health_score) }}>
                          <span>{previousSummary.health_score}</span>
                        </div>
                        <span className="hs-compare-score-label" style={{ color: getScoreColor(previousSummary.health_score) }}>
                          {previousSummary.health_score_label}
                        </span>
                        <span className="hs-compare-date">
                          {new Date(previousSummary.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="hs-compare-arrow">
                        {(() => {
                          const change = getChangeIndicator(summary.health_score, previousSummary.health_score);
                          return (
                            <div className="hs-compare-change" style={{ color: change.color }}>
                              <FontAwesomeIcon icon={change.icon} />
                              <span className="hs-compare-change-value">{change.diff}</span>
                              <span className="hs-compare-change-pct">{change.label}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="hs-compare-score-card">
                        <span className="hs-compare-label">Current</span>
                        <div className="hs-compare-score-circle" style={{ background: getScoreGradient(summary.health_score) }}>
                          <span>{summary.health_score}</span>
                        </div>
                        <span className="hs-compare-score-label" style={{ color: getScoreColor(summary.health_score) }}>
                          {summary.health_score_label}
                        </span>
                        <span className="hs-compare-date">
                          {new Date(summary.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Vitals Trend Comparison */}
                  {summary.vitals_summary?.raw && Object.keys(summary.vitals_summary.raw).length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faHeartPulse} />
                        <h3>Vitals Trend Analysis</h3>
                      </div>
                      <div className="hs-compare-table">
                        <div className="hs-compare-table-header">
                          <span>Vital</span>
                          <span>Previous</span>
                          <span>Current</span>
                          <span>Trend</span>
                          <span>Status</span>
                        </div>
                        {Object.entries(summary.vitals_summary.raw).map(([key, data]: [string, any]) => {
                          const prevRaw = previousSummary.vitals_summary?.raw?.[key];
                          const currentVal = key === 'blood_pressure'
                            ? `${data.latest?.systolic}/${data.latest?.diastolic}`
                            : data.latest?.value;
                          const prevVal = prevRaw
                            ? (key === 'blood_pressure'
                              ? `${prevRaw.latest?.systolic}/${prevRaw.latest?.diastolic}`
                              : prevRaw.latest?.value)
                            : '-';
                          const trend = data.summary?.trend || data.trend_systolic || 'no_trend';
                          const trendInfo = getTrendLabel(trend);
                          const unit = key === 'blood_pressure' ? 'mmHg' : (data.latest?.unit || '');

                          // Determine if improvement or decline for this vital
                          const currentOverview = summary.vitals_summary?.overview?.find(
                            (v: VitalOverview) => v.vital.toLowerCase().includes(key.replace(/_/g, ' '))
                          );
                          const status = currentOverview?.status || 'unknown';

                          return (
                            <div key={key} className="hs-compare-table-row">
                              <span className="hs-compare-vital-name">{formatVitalName(key)}</span>
                              <span className="hs-compare-value">{prevVal} <small>{unit}</small></span>
                              <span className="hs-compare-value">{currentVal} <small>{unit}</small></span>
                              <span className="hs-compare-trend" style={{ color: trendInfo.color }}>
                                <FontAwesomeIcon icon={trendInfo.icon} />
                                {trendInfo.label}
                              </span>
                              <span
                                className="hs-vital-badge"
                                style={{
                                  color: getVitalStatusColor(status),
                                  background: `${getVitalStatusColor(status)}15`,
                                }}
                              >
                                {status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Risk Flags Comparison */}
                  <div className="hs-section">
                    <div className="hs-section-header">
                      <FontAwesomeIcon icon={faShieldHeart} />
                      <h3>Risk Flags Change</h3>
                    </div>
                    <div className="hs-compare-risk-summary">
                      <div className="hs-compare-risk-stat">
                        <span className="hs-compare-risk-count" style={{ color: '#ef4444' }}>
                          {previousSummary.risk_flags?.filter((f: RiskFlag) => f.level === 'high').length || 0}
                          <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '0.6rem', margin: '0 4px' }} />
                          {summary.risk_flags?.filter((f: RiskFlag) => f.level === 'high').length || 0}
                        </span>
                        <span>High Risk</span>
                      </div>
                      <div className="hs-compare-risk-stat">
                        <span className="hs-compare-risk-count" style={{ color: '#f59e0b' }}>
                          {previousSummary.risk_flags?.filter((f: RiskFlag) => f.level === 'medium').length || 0}
                          <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '0.6rem', margin: '0 4px' }} />
                          {summary.risk_flags?.filter((f: RiskFlag) => f.level === 'medium').length || 0}
                        </span>
                        <span>Medium Risk</span>
                      </div>
                      <div className="hs-compare-risk-stat">
                        <span className="hs-compare-risk-count" style={{ color: '#3b82f6' }}>
                          {previousSummary.risk_flags?.filter((f: RiskFlag) => f.level === 'low').length || 0}
                          <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '0.6rem', margin: '0 4px' }} />
                          {summary.risk_flags?.filter((f: RiskFlag) => f.level === 'low').length || 0}
                        </span>
                        <span>Low Risk</span>
                      </div>
                    </div>
                  </div>

                  {/* Data Sources Comparison */}
                  <div className="hs-section">
                    <div className="hs-section-header">
                      <FontAwesomeIcon icon={faDatabase} />
                      <h3>Data Sources Used</h3>
                    </div>
                    <div className="hs-compare-sources">
                      <div>
                        <h4 className="hs-subsection-title">Previous Report</h4>
                        <div className="hs-data-sources">
                          {previousSummary.data_sources.map(s => (
                            <span key={s} className="hs-source-tag">{s.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="hs-subsection-title">Current Report</h4>
                        <div className="hs-data-sources">
                          {summary.data_sources.map(s => (
                            <span key={s} className="hs-source-tag">{s.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== Raw Data Tab ===== */}
          {activeTab === 'rawdata' && (
            <div className="hs-content">
              <div className="hs-rawdata-intro">
                <FontAwesomeIcon icon={faDatabase} />
                <p>All data points and vitals used to generate this health insights report are shown below for full transparency.</p>
              </div>

              {/* Raw Vitals Data */}
              {summary.vitals_summary?.raw && Object.keys(summary.vitals_summary.raw).length > 0 && (
                <div className="hs-section">
                  <div className="hs-section-header">
                    <FontAwesomeIcon icon={faHeartPulse} />
                    <h3>Vital Signs Data</h3>
                  </div>
                  <div className="hs-raw-vitals-grid">
                    {Object.entries(summary.vitals_summary.raw).map(([key, data]: [string, any]) => (
                      <div key={key} className="hs-raw-vital-card">
                        <div className="hs-raw-vital-header">
                          <h4>{formatVitalName(key)}</h4>
                          {data.summary?.trend && (
                            <span className="hs-raw-trend" style={{ color: getTrendLabel(data.summary.trend).color }}>
                              <FontAwesomeIcon icon={getTrendLabel(data.summary.trend).icon} />
                              {getTrendLabel(data.summary.trend).label}
                            </span>
                          )}
                        </div>
                        <div className="hs-raw-vital-stats">
                          {key === 'blood_pressure' ? (
                            <>
                              <div className="hs-raw-stat">
                                <span className="hs-raw-stat-label">Latest</span>
                                <span className="hs-raw-stat-value">{data.latest?.systolic}/{data.latest?.diastolic} mmHg</span>
                              </div>
                              <div className="hs-raw-stat">
                                <span className="hs-raw-stat-label">Type</span>
                                <span className="hs-raw-stat-value">{data.latest?.type || '-'}</span>
                              </div>
                              {data.latest?.category && (
                                <div className="hs-raw-stat">
                                  <span className="hs-raw-stat-label">Category</span>
                                  <span className="hs-raw-stat-value">{data.latest.category}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="hs-raw-stat">
                                <span className="hs-raw-stat-label">Latest</span>
                                <span className="hs-raw-stat-value">{data.latest?.value} {data.latest?.unit}</span>
                              </div>
                              {data.summary && (
                                <>
                                  <div className="hs-raw-stat">
                                    <span className="hs-raw-stat-label">Min</span>
                                    <span className="hs-raw-stat-value">{data.summary.min} {data.summary.unit || data.latest?.unit}</span>
                                  </div>
                                  <div className="hs-raw-stat">
                                    <span className="hs-raw-stat-label">Max</span>
                                    <span className="hs-raw-stat-value">{data.summary.max} {data.summary.unit || data.latest?.unit}</span>
                                  </div>
                                  <div className="hs-raw-stat">
                                    <span className="hs-raw-stat-label">Average</span>
                                    <span className="hs-raw-stat-value">{data.summary.average} {data.summary.unit || data.latest?.unit}</span>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                          <div className="hs-raw-stat">
                            <span className="hs-raw-stat-label">Records</span>
                            <span className="hs-raw-stat-value">{data.record_count || '-'} total</span>
                          </div>
                          <div className="hs-raw-stat">
                            <span className="hs-raw-stat-label">Last Updated</span>
                            <span className="hs-raw-stat-value">{data.latest?.date || '-'}</span>
                          </div>
                          {key === 'bmi' && data.latest?.category && (
                            <div className="hs-raw-stat">
                              <span className="hs-raw-stat-label">Category</span>
                              <span className="hs-raw-stat-value">{data.latest.category}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw Health Data (from raw_health_data field) */}
              {summary.raw_health_data && (
                <>
                  {/* Prescriptions */}
                  {summary.raw_health_data.prescriptions && summary.raw_health_data.prescriptions.length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faClipboardList} />
                        <h3>Prescriptions & Lab Results ({summary.raw_health_data.prescriptions.length})</h3>
                      </div>
                      <div className="hs-raw-table">
                        {summary.raw_health_data.prescriptions.map((rx: any, i: number) => (
                          <div key={i} className="hs-raw-table-row">
                            <div className="hs-raw-table-main">
                              <strong>{rx.record_name}</strong>
                              <span className="hs-raw-meta">{rx.record_type} | {rx.record_date || 'No date'}</span>
                              {rx.doctor_name && <span className="hs-raw-meta"><FontAwesomeIcon icon={faUserDoctor} /> {rx.doctor_name} {rx.doctor_specialization && `(${rx.doctor_specialization})`}</span>}
                            </div>
                            {rx.parameters && rx.parameters.length > 0 && (
                              <div className="hs-raw-params">
                                {rx.parameters.map((p: any, j: number) => (
                                  <div key={j} className="hs-raw-param">
                                    <span>{p.name}</span>
                                    <span className="hs-raw-param-result">{p.result} {p.unit}</span>
                                    {(p.start_range || p.end_range) && (
                                      <span className="hs-raw-param-range">Range: {p.start_range} - {p.end_range}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hospitalizations */}
                  {summary.raw_health_data.hospitalizations && summary.raw_health_data.hospitalizations.length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faHospital} />
                        <h3>Hospitalization Records ({summary.raw_health_data.hospitalizations.length})</h3>
                      </div>
                      <div className="hs-raw-table">
                        {summary.raw_health_data.hospitalizations.map((h: any, i: number) => (
                          <div key={i} className="hs-raw-table-row">
                            <div className="hs-raw-table-main">
                              <strong>{h.name}</strong>
                              <span className="hs-raw-meta">{h.type} | {h.hospital}</span>
                              <span className="hs-raw-meta">Admitted: {h.admitted || 'N/A'} | Discharged: {h.discharged || 'N/A'}</span>
                              {h.reason && <span className="hs-raw-meta">Reason: {h.reason}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vaccinations */}
                  {summary.raw_health_data.vaccinations && summary.raw_health_data.vaccinations.length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faSyringe} />
                        <h3>Vaccination Records ({summary.raw_health_data.vaccinations.length})</h3>
                      </div>
                      <div className="hs-raw-table">
                        {summary.raw_health_data.vaccinations.map((v: any, i: number) => (
                          <div key={i} className="hs-raw-table-row">
                            <div className="hs-raw-table-main">
                              <strong>{v.name}</strong>
                              <span className="hs-raw-meta">Date: {v.date || 'N/A'} | Dose {v.dose_number}/{v.total_doses}</span>
                              {v.next_due && <span className="hs-raw-meta">Next due: {v.next_due}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Health Assessment */}
                  {summary.raw_health_data.health_assessment && Object.keys(summary.raw_health_data.health_assessment).length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faClipboardList} />
                        <h3>Health Assessment Data</h3>
                      </div>
                      <div className="hs-raw-vital-stats">
                        {Object.entries(summary.raw_health_data.health_assessment).map(([key, val]: [string, any]) => (
                          <div key={key} className="hs-raw-stat">
                            <span className="hs-raw-stat-label">{formatVitalName(key)}</span>
                            <span className="hs-raw-stat-value">{String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Insurance */}
                  {summary.raw_health_data.insurance && summary.raw_health_data.insurance.length > 0 && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faShieldHalved} />
                        <h3>Insurance Policies ({summary.raw_health_data.insurance.length})</h3>
                      </div>
                      <div className="hs-raw-table">
                        {summary.raw_health_data.insurance.map((ins: any, i: number) => (
                          <div key={i} className="hs-raw-table-row">
                            <div className="hs-raw-table-main">
                              <strong>{ins.policy_name}</strong>
                              <span className="hs-raw-meta">{ins.type} | Sum Assured: {ins.sum_assured || 'N/A'}</span>
                              <span className="hs-raw-meta">Valid until: {ins.valid_until || 'N/A'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Wearable Raw Data */}
                  {summary.raw_health_data.wearable && summary.raw_health_data.wearable.has_wearable && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faDumbbell} />
                        <h3>Wearable Device Data</h3>
                      </div>
                      <div className="hs-raw-vitals-grid">
                        {Object.entries(summary.raw_health_data.wearable)
                          .filter(([key]) => key !== 'has_wearable')
                          .map(([key, data]: [string, any]) => (
                            <div key={key} className="hs-raw-vital-card">
                              <div className="hs-raw-vital-header">
                                <h4>{formatVitalName(key)}</h4>
                              </div>
                              <div className="hs-raw-vital-stats">
                                <div className="hs-raw-stat">
                                  <span className="hs-raw-stat-label">Days of Data</span>
                                  <span className="hs-raw-stat-value">{data.days_of_data}</span>
                                </div>
                                <div className="hs-raw-stat">
                                  <span className="hs-raw-stat-label">Average</span>
                                  <span className="hs-raw-stat-value">{data.average || '-'}</span>
                                </div>
                                {data.latest && (
                                  <>
                                    <div className="hs-raw-stat">
                                      <span className="hs-raw-stat-label">Latest Avg</span>
                                      <span className="hs-raw-stat-value">{data.latest.avg || '-'} {data.latest.unit}</span>
                                    </div>
                                    <div className="hs-raw-stat">
                                      <span className="hs-raw-stat-label">Latest Min/Max</span>
                                      <span className="hs-raw-stat-value">{data.latest.min || '-'} / {data.latest.max || '-'}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Profile Data */}
                  {summary.raw_health_data.profile && (
                    <div className="hs-section">
                      <div className="hs-section-header">
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <h3>Profile Data Used</h3>
                      </div>
                      <div className="hs-raw-vital-stats">
                        {Object.entries(summary.raw_health_data.profile).map(([key, val]: [string, any]) => (
                          <div key={key} className="hs-raw-stat">
                            <span className="hs-raw-stat-label">{formatVitalName(key)}</span>
                            <span className="hs-raw-stat-value">{String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Fallback: show what's in the existing summary fields if no raw_health_data */}
              {!summary.raw_health_data && (
                <div className="hs-section">
                  <div className="hs-empty-text">
                    <p>Detailed raw data is available for newly generated reports. Regenerate your health insights to see complete raw data.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="hs-content">
              <div className="hs-section">
                <div className="hs-section-header">
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                  <h3>Insights History</h3>
                </div>
                {history.length === 0 ? (
                  <p className="hs-empty-text">No previous insights found.</p>
                ) : (
                  <div className="hs-history-list">
                    {history.map((item) => (
                      <button
                        key={item.id}
                        className={`hs-history-card ${summary?.id === item.id ? 'active' : ''}`}
                        onClick={() => loadHistoryItem(item.id)}
                      >
                        <div
                          className="hs-history-score"
                          style={{ background: getScoreGradient(item.health_score) }}
                        >
                          {item.health_score}
                        </div>
                        <div className="hs-history-info">
                          <strong>{item.health_score_label}</strong>
                          <span>
                            {new Date(item.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </span>
                          <div className="hs-history-sources">
                            {item.data_sources.slice(0, 3).map((s) => (
                              <span key={s} className="hs-source-tag small">{s.replace(/_/g, ' ')}</span>
                            ))}
                            {item.data_sources.length > 3 && (
                              <span className="hs-source-tag small">+{item.data_sources.length - 3}</span>
                            )}
                          </div>
                        </div>
                        <span className={`hs-status-badge ${item.generation_status}`}>
                          {item.generation_status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {summary && summary.generation_status === 'failed' && (
        <div className="hs-failed">
          <FontAwesomeIcon icon={faCircleExclamation} size="2x" />
          <h3>Summary Generation Failed</h3>
          <p>{summary.error_message || 'An unexpected error occurred. Please try again.'}</p>
          <button className="hs-generate-btn" onClick={handleGenerateClick}>
            <FontAwesomeIcon icon={faRotate} />
            Try Again
          </button>
        </div>
      )}

      {/* Disclaimer */}
      <div className="hs-disclaimer">
        <FontAwesomeIcon icon={faCircleInfo} />
        <p>
          This health insight is generated using AI analysis and is for informational purposes only.
          It does not constitute medical advice. Always consult a qualified healthcare professional
          for medical decisions.
        </p>
      </div>
    </div>
    </>
  );
};

export default HealthSummary;
